interface IToneOptions {
  type?: OscillatorType;
  volume?: number;
  delay?: number;
  fadeIn?: number;
  fadeOut?: number;
}

let audioCtx: AudioContext | null = null;

const getContext = (): AudioContext => {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  if (audioCtx!.state === "suspended") {
    audioCtx!.resume();
  }
  return audioCtx!;
};

const playTone = (
  frequency: number,
  duration: number,
  { type = "sine", volume = 0.15, delay = 0, fadeIn = 0.02, fadeOut }: IToneOptions = {},
): void => {
  const ctx = getContext();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.value = frequency;
  osc.connect(gain);
  gain.connect(ctx.destination);

  const startTime = ctx.currentTime + delay;
  const endFade = fadeOut ?? duration * 0.4;

  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(volume, startTime + fadeIn);
  gain.gain.setValueAtTime(volume, startTime + duration - endFade);
  gain.gain.linearRampToValueAtTime(0, startTime + duration);

  osc.start(startTime);
  osc.stop(startTime + duration);
};

const playChord = (
  frequencies: number[],
  duration: number,
  options: IToneOptions = {},
): void => {
  const vol = (options.volume || 0.15) / frequencies.length;
  frequencies.forEach((freq) => {
    playTone(freq, duration, { ...options, volume: vol });
  });
};

// --- Sound effects ---

// Soft tick for countdown (3, 2, 1)
const playCountdownTick = () => {
  playTone(880, 0.12, { volume: 0.1, fadeOut: 0.06 });
};

// "Go!" sound — bright ascending
const playCountdownGo = () => {
  playTone(523.25, 0.15, { volume: 0.12, delay: 0 });
  playTone(659.25, 0.15, { volume: 0.12, delay: 0.1 });
  playTone(783.99, 0.25, { volume: 0.14, delay: 0.2 });
};

// Wheel spinning — gentle melodic ticks that decelerate
const playWheelSpin = () => {
  const totalTicks = 18;
  const notes = [523.25, 587.33, 659.25, 698.46, 783.99, 880];
  for (let i = 0; i < totalTicks; i++) {
    const progress = i / totalTicks;
    // Decelerate: ticks get further apart over time
    const delay = progress * 2.5 + (progress * progress) * 1.2;
    const freq = notes[i % notes.length];
    const vol = 0.07 - progress * 0.03;
    playTone(freq, 0.08, { type: "triangle", volume: Math.max(vol, 0.02), delay, fadeOut: 0.04 });
  }
};

// Wheel landed — warm resolved chord
const playWheelLand = () => {
  playChord([523.25, 659.25, 783.99], 0.6, { volume: 0.18, delay: 0 });
};

// Opponent answered — subtle soft ping
const playOpponentAnswered = () => {
  playTone(1046.5, 0.1, { volume: 0.06, fadeOut: 0.05 });
};

// Round won — gentle ascending arpeggio
const playRoundWon = () => {
  playTone(523.25, 0.2, { volume: 0.1, delay: 0 });
  playTone(659.25, 0.2, { volume: 0.1, delay: 0.12 });
  playTone(783.99, 0.3, { volume: 0.12, delay: 0.24 });
};

// Round lost — soft descending two-note
const playRoundLost = () => {
  playTone(440, 0.25, { volume: 0.08, delay: 0 });
  playTone(349.23, 0.35, { volume: 0.08, delay: 0.15 });
};

// Round draw — neutral two-note
const playRoundDraw = () => {
  playTone(440, 0.25, { volume: 0.08, delay: 0 });
  playTone(440, 0.25, { volume: 0.08, delay: 0.2 });
};

// Victory — celebratory ascending arpeggio with final chord
const playVictory = () => {
  playTone(523.25, 0.15, { volume: 0.1, delay: 0 });
  playTone(659.25, 0.15, { volume: 0.1, delay: 0.12 });
  playTone(783.99, 0.15, { volume: 0.1, delay: 0.24 });
  playTone(1046.5, 0.2, { volume: 0.12, delay: 0.36 });
  playChord([523.25, 659.25, 783.99, 1046.5], 0.8, { volume: 0.2, delay: 0.5 });
};

// Defeat — gentle descending notes
const playDefeat = () => {
  playTone(523.25, 0.25, { volume: 0.08, delay: 0 });
  playTone(440, 0.25, { volume: 0.08, delay: 0.2 });
  playTone(349.23, 0.4, { volume: 0.07, delay: 0.4 });
};

// Draw game — neutral warm resolution
const playDrawGame = () => {
  playChord([440, 523.25, 659.25], 0.8, { volume: 0.15, delay: 0 });
};

// Ready confirmation — soft double ping
const playReady = () => {
  playTone(783.99, 0.1, { volume: 0.08, delay: 0 });
  playTone(1046.5, 0.12, { volume: 0.1, delay: 0.1 });
};

// Timer warning — last 5 seconds, gentle urgent pulse
const playTimerWarning = () => {
  playTone(740, 0.08, { volume: 0.07, fadeOut: 0.04 });
};

// Duel invite received — bright, friendly two-note "ta-da" to draw attention
const playInviteReceived = () => {
  playTone(659.25, 0.14, { volume: 0.12, delay: 0 });
  playTone(987.77, 0.28, { volume: 0.14, delay: 0.13 });
};

const duelSounds = {
  countdownTick: playCountdownTick,
  countdownGo: playCountdownGo,
  wheelSpin: playWheelSpin,
  wheelLand: playWheelLand,
  opponentAnswered: playOpponentAnswered,
  roundWon: playRoundWon,
  roundLost: playRoundLost,
  roundDraw: playRoundDraw,
  victory: playVictory,
  defeat: playDefeat,
  drawGame: playDrawGame,
  ready: playReady,
  timerWarning: playTimerWarning,
  inviteReceived: playInviteReceived,
};

export default duelSounds;
